import React from "react";
import { LogOut, Anchor, Shield, Compass } from "lucide-react";
import { useApp } from "@/lib/AppContext";
import { t, ROLE_LABEL } from "@/lib/i18n";

const ROLE_ICON = { scout: Compass, operator: Shield };

export default function Header() {
  const { user, lang, setLang, logout } = useApp();
  const RoleIcon = user ? ROLE_ICON[user.role] || Compass : Compass;

  return (
    <header
      className="sticky top-0 z-40 border-b border-[#1e3a61] bg-[#0a1628]/85 backdrop-blur-xl"
      data-testid="portal-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative w-11 h-11 rounded-xl border border-[#d4a843]/50 bg-gradient-to-br from-[#132744] to-[#0a1628] grid place-items-center shadow-lg shrink-0">
            <Anchor className="w-5 h-5 text-[#f3c669]" />
            <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#d4a843] shadow-[0_0_10px_2px_rgba(212,168,67,0.6)]" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-display text-lg font-bold tracking-tight text-white truncate">
              {t(lang, "brand")}
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#d4a843]/80 truncate">
              {t(lang, "brand_sub")} · {t(lang, "domain_tag")}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="inline-flex rounded-xl border border-[#1e3a61] p-0.5 bg-[#0f2038]"
            role="tablist"
            aria-label={t(lang, "lang_toggle_label")}
          >
            {["en", "vi"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`language-toggle-${code}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  lang === code
                    ? "bg-gradient-to-r from-[#d4a843] to-[#f3c669] text-[#0a1628]"
                    : "text-slate-300 hover:text-[#f3c669]"
                }`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          {user && (
            <>
              <div
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#d4a843]/40 bg-[#d4a843]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-[#f3c669]"
                data-testid="role-badge"
              >
                <RoleIcon className="w-3.5 h-3.5" /> {ROLE_LABEL(lang, user.role)}
              </div>
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-[10px] uppercase tracking-widest text-slate-400">
                  {ROLE_LABEL(lang, user.role)}
                </span>
                <span className="text-sm font-semibold text-white" data-testid="user-name">
                  {user.name}
                </span>
              </div>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="ghost-btn inline-flex items-center gap-2 text-sm"
                aria-label={t(lang, "logout")}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t(lang, "logout")}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
